import type { CartTags } from '@iheartjane/data-access';
import { extractTrackingTags } from '@iheartjane/tracking';
import type { AppMode } from '@iheartjane/types';
import { createExternalStore, Storage } from '@iheartjane/utils';
import isEmpty from 'lodash-es/isEmpty';
import { useSyncExternalStore } from 'react';

import { cartStorageKey } from '../cartStorageKey';

type StoreId = string | number | null | undefined;

// Only gates the no-partner-yet case; the key shape itself is `cartStorageKey`.
const PARTNER_SCOPED_MODES: readonly AppMode[] = [
  'embedded',
  'whiteLabel',
  'framelessEmbed',
];

const TAGS_SUFFIX = 'tags';
const URL_PROMO_CODE_SUFFIX = 'promo-code';

/**
 * The one key derivation for reads and writes. `null` while a partner-scoped
 * mode has no partner yet, so nothing is ever stored under `...-undefined-*`.
 */
const entryParamKey = (
  appMode: AppMode,
  storeId: StoreId,
  suffix: string
): string | null => {
  if (
    PARTNER_SCOPED_MODES.includes(appMode) &&
    (storeId == null || storeId === '')
  ) {
    return null;
  }

  return `${cartStorageKey(appMode, storeId ?? undefined)}-${suffix}`;
};

const { notify, stable, subscribe } = createExternalStore<
  CartTags | undefined
>();

export const getLocalTags = (
  appMode: AppMode | undefined,
  storeId: StoreId
): CartTags | undefined => {
  const key = appMode && entryParamKey(appMode, storeId, TAGS_SUFFIX);
  const stored = key ? Storage.get<CartTags>(key) : undefined;

  return stable(
    stored && typeof stored === 'object' && !isEmpty(stored)
      ? stored
      : undefined,
    key ?? ''
  );
};

/** Not the applied `cart.promo_code`: its only job is to auto-submit the form. */
export const getLocalUrlPromoCode = (
  appMode: AppMode | undefined,
  storeId: StoreId
): string | undefined => {
  const key = appMode && entryParamKey(appMode, storeId, URL_PROMO_CODE_SUFFIX);
  const stored = key ? Storage.get<string>(key) : undefined;

  return typeof stored === 'string' && stored ? stored : undefined;
};

/**
 * App-entry write. A non-empty URL value wins, otherwise storage is left as it
 * is: an empty query never erases earlier tags or URL promo code.
 */
export const saveEntryParams = (
  appMode: AppMode,
  storeId: StoreId,
  query: Record<string, string | undefined>
) => {
  const tagsKey = entryParamKey(appMode, storeId, TAGS_SUFFIX);
  const urlPromoCodeKey = entryParamKey(
    appMode,
    storeId,
    URL_PROMO_CODE_SUFFIX
  );
  const urlTags = extractTrackingTags(query);
  const urlPromoCode = query['promo_code'];

  if (tagsKey && !isEmpty(urlTags)) Storage.set(tagsKey, urlTags);
  if (urlPromoCodeKey && typeof urlPromoCode === 'string' && urlPromoCode) {
    Storage.set(urlPromoCodeKey, urlPromoCode);
  }

  notify();
};

/** Consumed means absent: there is no `null` state. */
export const consumeLocalUrlPromoCode = (
  appMode: AppMode,
  storeId: StoreId
) => {
  const key = entryParamKey(appMode, storeId, URL_PROMO_CODE_SUFFIX);
  if (key) Storage.remove(key);

  notify();
};

export const useLocalTags = (appMode: AppMode, storeId: StoreId) =>
  useSyncExternalStore(
    subscribe,
    () => getLocalTags(appMode, storeId),
    () => undefined
  );

export const useLocalUrlPromoCode = (appMode: AppMode, storeId: StoreId) =>
  useSyncExternalStore(
    subscribe,
    () => getLocalUrlPromoCode(appMode, storeId),
    () => undefined
  );
